import React from 'react';
import HyperLayout from '../components/HyperLayout';
import HeroSection from '../components/HeroSection';
import { Helmet } from 'react-helmet';
import useScrollToTop from '../hooks/useScrollToTop';

export function PrivacyPolicy() {
  // Use scroll to top hook
  useScrollToTop();
  
  return (
    <HyperLayout>
      <Helmet>
        <title>Privacy Policy | BasedApp</title>
        <meta name="description" content="Learn how BasedApp collects, uses, and protects your personal information. Our privacy policy outlines our commitment to data security and user privacy rights." />
        <link rel="canonical" href="https://basedapp.io/privacy-policy" />
      </Helmet>
      <HeroSection className="h-[300px]">
        <div className="max-w-screen-xl mt-auto mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-center text-white mb-6">Privacy Policy</h1>
          <p className="text-xl text-white text-center max-w-3xl mx-auto mb-16">
            Learn how BasedApp collects, uses, and protects your personal information.
          </p>
        </div>
      </HeroSection>
      <div className="min-h-screen bg-black">
        <div className="max-w-4xl mx-auto px-4 py-12 md:py-16">
          <div className="space-y-6 text-white text-sm leading-relaxed">
            <section>
              <p className="mb-6 text-lg">
                This Privacy Policy explains how BasedApp collects, uses, and shares your personal information. 
                We take your privacy seriously and are committed to protecting your personal data.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-white">1. Information We Collect</h2>
              <p className="mb-4">
                We collect information you provide directly to us when you:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Create an account or profile</li>
                <li>Use our services, including transaction data</li>
                <li>Communicate with us via email, social media, or other channels</li>
                <li>Complete verification processes</li>
                <li>Respond to surveys or participate in promotions</li>
              </ul>
              <p className="mb-4">
                We also automatically collect certain information when you use our services, including:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Device information (operating system, hardware model, device identifiers)</li>
                <li>Log information (IP address, browser type, pages visited)</li>
                <li>Location information when you use our app (if you've granted permission)</li>
                <li>Blockchain transaction information (which is publicly available)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-white">2. How We Use Your Information</h2>
              <p className="mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide, maintain, and improve our services</li>
                <li>Process transactions and send related information</li>
                <li>Verify your identity and comply with regulatory requirements</li>
                <li>Communicate with you about products, services, and events</li>
                <li>Respond to your comments, questions, and requests</li>
                <li>Prevent fraudulent transactions and enhance security</li>
                <li>Monitor and analyze trends, usage, and activities</li>
                <li>Personalize your experience and provide content and features</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-white">3. Information Sharing and Disclosure</h2>
              <p className="mb-4">
                We may share your information with:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Service providers who perform services on our behalf</li>
                <li>Financial institutions and payment processors to facilitate transactions</li>
                <li>Business partners with whom we offer co-branded services or promotions</li>
                <li>Law enforcement, government officials, or other third parties when required by law</li>
                <li>Other users as directed by you (such as public transaction information)</li>
              </ul>
              <p className="mt-4">
                We may also share aggregated or de-identified information, which cannot reasonably be used to identify you.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-white">4. Security</h2>
              <p className="mb-4">
                We take reasonable measures to help protect your personal information from loss, theft, misuse, unauthorized access, disclosure, alteration, and destruction. However, no security system is impenetrable, and we cannot guarantee the security of our systems or your information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-white">5. Your Rights</h2>
              <p className="mb-4">
                Depending on your location, you may have certain rights regarding your personal information, including:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Access to your personal information</li>
                <li>Correction of inaccurate or incomplete information</li>
                <li>Deletion of your personal information</li>
                <li>Restriction of processing of your personal information</li>
                <li>Data portability</li>
                <li>Objection to processing</li>
              </ul>
              <p className="mt-4">
                To exercise these rights, please contact us using the details provided in the "Contact Us" section.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-white">6. International Data Transfers</h2>
              <p className="mb-4">
                We may transfer your personal information to countries other than the one in which you live. We deploy appropriate safeguards when we transfer personal data.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-white">7. Changes to this Privacy Policy</h2>
              <p className="mb-4">
                We may change this Privacy Policy from time to time. If we make changes, we will notify you by revising the date at the top of the policy and, in some cases, we may provide you with additional notice.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-white">8. Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy, please contact us at <a href="mailto:support@basedapp.io" className="text-primary hover:underline">support@basedapp.io</a>.
              </p>
            </section>
            
            <div className="pt-6 text-sm text-white/60 border-t border-white/20">
              <p>Last updated: March 25, 2025</p>
            </div>
          </div>
        </div>
      </div>
    </HyperLayout>
  );
} 

export default PrivacyPolicy;